import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePracticeReflection } from '../../hooks/usePracticeReflection';
import { navigateTo } from '../../navigation/rootNavigation';
import { ReflectionModal } from './ReflectionModal';
import { ReflectionPrivacyModal } from './ReflectionPrivacyModal';
import {
  ReflectionLauncherContext, ReflectionVisibilityContext, holdWhilePending, nextVisibleWindows,
  type ReflectionLauncher, type ReflectionRequest, type ReflectionVisibility,
} from './reflectionLauncher';

/**
 * CORE-1751 — окно рефлексии у корня приложения: после выхода из Live-сессии и
 * по кнопке в сообщении Агапеллы.
 *
 * Те же два окна и та же логика, что после практики (`usePracticeReflection`):
 * сначала — выбор приватности, если человек его ещё не делал, потом вопросы.
 * Отличается только повод: здесь окно открывают по просьбе, а не по порогу
 * плеера, и id завершения приходит готовым с сервера.
 *
 * Каждая просьба — новый «материал»: её ключ сбрасывает «одно открытие на
 * практику» внутри хука. Иначе кнопка в сообщении, нажатая второй раз после
 * закрытия окна, ничего бы не открыла.
 */
interface ActiveRequest extends ReflectionRequest {
  key: string;
}

export const ReflectionLauncherProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [request, setRequest] = useState<ActiveRequest | null>(null);

  // CORE-1751 — какие окна рефлексии сейчас на экране: окно у корня
  // (`ReflectionWindow` ниже) и окна экранов практики. Каждое сообщает о себе
  // само, через `usePracticeReflection`; поздравление по Пути Света
  // (`GamificationCelebrationProvider`) ждёт, пока набор не опустеет.
  const [visibleWindows, setVisibleWindows] = useState<ReadonlySet<symbol>>(() => new Set());
  const report = useCallback((id: symbol, visible: boolean) => {
    setVisibleWindows((current) => nextVisibleWindows(current, id, visible));
  }, []);
  const visibility = useMemo<ReflectionVisibility>(
    () => ({ visible: visibleWindows.size > 0, report }),
    [visibleWindows, report],
  );

  return (
    <ReflectionVisibilityContext.Provider value={visibility}>
      <ReflectionWindow request={request} setRequest={setRequest}>{children}</ReflectionWindow>
    </ReflectionVisibilityContext.Provider>
  );
};

/**
 * Само окно у корня. Отдельным компонентом под провайдером видимости: хук
 * окна сообщает о нём через контекст, а контекст виден только детям.
 */
const ReflectionWindow: React.FC<{
  request: ActiveRequest | null;
  setRequest: (request: ActiveRequest) => void;
  children: React.ReactNode;
}> = ({ request, setRequest, children }) => {
  const { isAuthenticated } = useAuth();

  const reflection = usePracticeReflection({
    isAuthenticated,
    practiceCompletionId: request?.completionId ?? null,
    materialKey: request?.key ?? 'none',
    source: request?.source ?? 'practice',
  });

  // Открыть — уже после того, как хук увидел новый ключ и сбросил прошлое
  // открытие: эффекты идут в порядке объявления, хук объявлен выше.
  const { onPracticeCompleted } = reflection;
  useEffect(() => {
    if (request) onPracticeCompleted();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.key]);

  const openReflection = useCallback((next: ReflectionRequest) => {
    setRequest({ ...next, key: `${next.source}:${next.completionId}:${Date.now()}` });
  }, [setRequest]);

  // Возврат QA 23.10 — пока сервер решает, предлагать ли рефлексию после
  // Live-сессии, поздравление ждёт так же, как ждёт открытого окна.
  const { report } = useContext(ReflectionVisibilityContext);
  const hold = useCallback((pending: Promise<unknown>) => holdWhilePending(report, pending), [report]);

  const value = useMemo<ReflectionLauncher>(() => ({ openReflection, hold }), [openReflection, hold]);

  // «Мои записи» — вкладка «Дневник» своего профиля. Провайдер стоит выше
  // навигатора, поэтому переход — через ref контейнера, а не useNavigation.
  const openJournal = () => {
    reflection.close();
    navigateTo('Dashboard', { initialTab: 'journal' });
  };

  return (
    <ReflectionLauncherContext.Provider value={value}>
      {children}
      <ReflectionPrivacyModal
        visible={reflection.step === 'privacy'}
        privacyHint={reflection.prompts?.privacyHint ?? ''}
        onChoose={reflection.choosePrivacy}
        busy={reflection.savingPrivacy}
      />
      {reflection.prompts ? (
        <ReflectionModal
          visible={reflection.step === 'questions'}
          prompts={reflection.prompts}
          practiceCompletionId={request?.completionId ?? null}
          onClose={reflection.close}
          onSave={reflection.save}
          onOpenJournal={openJournal}
        />
      ) : null}
    </ReflectionLauncherContext.Provider>
  );
};
